import React, { useState } from 'react';
import { ResourceForm, ResourceFormProps } from './ResourceForm';

interface ListWithAddEditRemoveProps<T> {
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  resourceFormFields: ResourceFormProps<T>["fields"];
  onAdd: (data: T) => Promise<void>;
  onEdit: (id: string, data: T) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  addTitle?: string;
  editTitle?: string;
  addButtonLabel?: string;
  editButtonLabel?: string;
  submitLabel?: string;
  renderAddForm?: (onAdd: () => void, addMode: boolean, setAddMode: (v: boolean) => void) => React.ReactNode;
}

export function ListWithAddEditRemove<T extends Record<string, any>>({
  items,
  getKey,
  renderItem,
  resourceFormFields,
  onAdd,
  onEdit,
  onRemove,
  addTitle = 'Add New',
  editTitle = 'Edit',
  addButtonLabel = 'Add +',
  editButtonLabel = 'Edit',
  submitLabel = 'Save',
  renderAddForm,
}: ListWithAddEditRemoveProps<T>) {
  const [addMode, setAddMode] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editResource, setEditResource] = useState<T | null>(null);

  return (
    <div>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {items.map((item) => {
          const key = getKey(item);
          if (editId === key) {
            return (
              <li key={key} style={{ marginBottom: '1em' }}>
                <ResourceForm<T>
                  resource={editResource}
                  isEditMode={true}
                  onSubmit={async (data) => {
                    await onEdit(key, data);
                    setEditId(null);
                    setEditResource(null);
                  }}
                  onCancel={() => {
                    setEditId(null);
                    setEditResource(null);
                  }}
                  fields={resourceFormFields}
                  title={editTitle}
                  submitLabel={submitLabel}
                />
              </li>
            );
          }
          return (
            <li key={key} style={{ marginBottom: '1em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>{renderItem(item)}</div>
              <div style={{ marginLeft: '1em', display: 'flex', gap: '0.5em' }}>
                <button onClick={() => { setEditId(key); setEditResource(item); }}>{editButtonLabel}</button>
                <button onClick={() => onRemove(key)}>Remove</button>
              </div>
            </li>
          );
        })}
      </ul>
      {!addMode && (
        <button onClick={() => setAddMode(true)}>{addButtonLabel}</button>
      )}
      {addMode && (
        <div style={{ marginTop: '1em' }}>
          {renderAddForm
            ? renderAddForm(async () => setAddMode(false), addMode, setAddMode)
            : (
              <ResourceForm<T>
                resource={null}
                isEditMode={false}
                onSubmit={async (data) => {
                  await onAdd(data);
                  setAddMode(false);
                }}
                onCancel={() => setAddMode(false)}
                fields={resourceFormFields}
                title={addTitle}
                submitLabel={submitLabel}
              />
            )}
        </div>
      )}
    </div>
  );
} 